import { getCookie, setCookie } from '../../library/function/cookie';
import { detectExitIntent, detectAdBlock } from '../../library/function/browser';

/**
 * FAB Container
 * @class FABContainer
 * @static
 * @method get_container_animation
 * @method get_container_class_name
 * @method get_container_data
 * @method ripple_dom_animation
 */
export default class FABContainer {
  /**
   * Auto Trigger Cookie Keys
   * - This is used to store the cookie key for each trigger type
   *
   * @type {Object}
   */
  static autoTriggerCookieKeys = {
    adblock: `FABTriggerAdblock`,
    exit_intent: `FABTriggerExitIntent`,
    time_delay: `FABTriggerTimeDelay`,
  };

  /**
   * Get the container animation
   * @param {Object} animation
   * @returns {Object}
   */
  static get_container_animation = (animation) => {
    animation.in = animation.in || '';
    animation.out = animation.out || '';
    animation.timeout = animation.timeout || 250;

    return animation;
  };

  /**
   * Get the class name
   * @param {String} classNames
   * @param {String} shape
   * @returns {String}
   */
  static get_container_class_name = (classNames, shape) => {
    let { fab_design } = window.FAB_PLUGIN.options;

    // Set default class name.
    classNames += ' '; // Avoid no space between class names.
    classNames += `flex items-center place-content-center relative rounded-full cursor-pointer `;

    // Add animate class, this is default by animate.css.
    classNames += `animate__animated `;

    // Set container shape if using shape template.
    if (fab_design.template.name === 'shape' && shape) {
      classNames += `fab-template-shape-${shape} `;
    }

    return classNames;
  };

  /**
   * Get the container data attribute
   * - Data attribute is used to store fab data such as ...
   * @param {Object} data
   * @returns {Object}
   */
  static get_container_data = (data) => {
    return data ? Object.fromEntries(Object.entries(data).map(([key, value]) => [`data-${key}`, value])) : {};
  };

  /**
   * Ripple DOM Animation
   * @param {Object} animation
   * @param {Boolean} active
   * @param {String} backgroundColor
   * @returns {String}
   */
  static ripple_dom_animation = (animation, active, backgroundColor) => {
    return animation.in === 'ripple' && active
      ? `
              <div class="animation-ripple" style="background: ${backgroundColor};"></div>
              <div class="animation-ripple" style="background: ${backgroundColor}; animation-delay: 0.6s;"></div>
          `
      : '';
  };

  /**
   * Auto Trigger Type
   * - This is used to check for auto trigger type configuration
   *
   * @param {Object} fab
   * @returns {Void}
   */
  static autotriggerType(fab) {
    // Check if there is auto trigger setting and match the trigger type
    const triggerTypes = ['time_delay', 'exit_intent', 'adblock'];
    if (!fab.trigger || !triggerTypes.includes(fab.trigger.type)) {
      return;
    }

    // Execute auto trigger type
    switch (fab.trigger.type) {
      case 'time_delay':
        this.autoTrigger(fab);
        break;
      case 'exit_intent':
        const cleanup = detectExitIntent(
          () => {
            this.autoTrigger(fab);
            setTimeout(cleanup, 500);
          },
          {
            sensitivity: 20,
            maxEvents: 2,
            delay: 1000,
          }
        );

        break;
      case 'adblock':
        detectAdBlock().then((detected) => {
          if (detected) {
            this.autoTrigger(fab);
          }
        });

        break;
    }
  }

  /**
   * Auto Trigger
   * - Auto trigger can only be triggered once
   * - Auto trigger will consider cookie existance and expiration
   *
   * @param {Object} fab
   * @returns {Void}
   */
  static autoTrigger(fab) {
    // Get cookies
    let cookieKey = `${this.autoTriggerCookieKeys[fab.trigger.type]}-${fab.ID}`;
    let cookie = getCookie(cookieKey);

    // Early return of has been triggered
    if (fab.trigger.triggered) {
      return;
    }

    // Early return of cookie is set or expiration > 0
    if (cookie && fab.trigger.cookie.expiration > 0) {
      return;
    }

    // Set triggered to true, this is to prevent multiple auto trigger
    fab.trigger.triggered = true;

    // Execute auto trigger
    let delay = fab.trigger.delay !== undefined ? fab.trigger.delay.replace('ms', '') : 1000;
    setTimeout(() => {
      setCookie(cookieKey, 'true', fab.trigger.cookie.expiration, '/');
      jQuery(`#fab-link-${fab.ID}`).click();
    }, delay);
  }
}
